import { type WindowLabel, WindowLabels } from '@electron/shared';
import { MEETING_NOTES_MIN_MACOS_VERSION } from '@electron/shared/meetings.types';
import { isVersionAtLeast } from '@electron/shared/utils';

import { getEnvVar } from '@/env';

export const inBrowser = () => !inElectron();
export const inElectron = () => {
  // Require BOTH the Electron user-agent AND the preload-injected `window.authApi`.
  // Cursor's built-in browser is itself Electron, so the UA alone gives false positives
  // when Littlebird's web build runs inside another Electron host without our preload.
  return (
    typeof navigator === 'object' &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.toLowerCase().includes(' electron/') &&
    typeof window !== 'undefined' &&
    typeof window.authApi !== 'undefined'
  );
};

type SupportedPlatform = 'macos' | 'android' | 'ios' | 'web' | 'electron';

export enum AppCapability {
  // Core capabilities
  ContextCapture = 'CONTEXT_CAPTURE',
  VoiceInput = 'VOICE_INPUT',
  CheckForUpdates = 'CHECK_FOR_UPDATES',
  FocusChatListener = 'FOCUS_CHAT_LISTENER',
  BackgroundAuthTokenRefresh = 'BACKGROUND_AUTH_TOKENS_REFRESH',
  StartupAtLogin = 'STARTUP_AT_LOGIN',
  HummingBirdCommandShortcut = 'HUMMING_BIRD_COMMAND_SHORTCUT',
  HotKeys = 'HOT_KEYS',
  Onboarding = 'ONBOARDING',
  WebOnboarding = 'WEB_ONBOARDING',
  AutoUpdate = 'AUTO_UPDATE',
  TitleBar = 'TITLEBAR',
  ContextMenu = 'CONTEXT_MENU',
  MeetingNotes = 'MEETING_NOTES',
  RecordMeeting = 'RECORD_MEETING',

  // Platform-specific capabilities
  DesktopExclusive = 'DESKTOP_EXCLUSIVE',
  MobileExclusive = 'MOBILE_EXCLUSIVE',
  WebExclusive = 'WEB_EXCLUSIVE',

  // Window-specific capabilities
  MainWindowExclusive = 'MAIN_WINDOW_EXCLUSIVE',
  DesktopMainWindowExclusive = 'DESKTOP_MAIN_WINDOW_EXCLUSIVE',
  SpotlightExclusive = 'SPOTLIGHT_EXCLUSIVE',
  MiniChatExclusive = 'MINI_CHAT_EXCLUSIVE',
}

const capabilityConstraintsMap: Record<
  AppCapability,
  {
    supportedPlatforms: SupportedPlatform[];
    supportedWindows: WindowLabel[];
    disabled?: boolean;
  }
> = {
  [AppCapability.ContextCapture]: {
    supportedPlatforms: ['macos', 'electron', 'android'],
    supportedWindows: ['main'],
  },
  [AppCapability.VoiceInput]: {
    supportedPlatforms: ['macos', 'electron', 'android'],
    supportedWindows: ['main'],
  },
  [AppCapability.CheckForUpdates]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.FocusChatListener]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.HummingBirdCommandShortcut]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: getEnvVar('VITE_ENV') === 'development' ? ['main'] : [],
    disabled: true,
  },
  [AppCapability.StartupAtLogin]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.HotKeys]: {
    supportedPlatforms: ['macos', 'electron', 'web'],
    supportedWindows: ['main', 'hummingbird', 'meeting-status'],
  },
  [AppCapability.Onboarding]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.WebOnboarding]: {
    supportedPlatforms: ['web'],
    supportedWindows: ['main'],
  },
  [AppCapability.AutoUpdate]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.TitleBar]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.ContextMenu]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.DesktopExclusive]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.MobileExclusive]: {
    supportedPlatforms: ['android', 'ios'],
    supportedWindows: ['main'],
  },
  [AppCapability.WebExclusive]: {
    supportedPlatforms: ['web'],
    supportedWindows: ['main'],
  },
  [AppCapability.MainWindowExclusive]: {
    supportedPlatforms: ['macos', 'electron', 'android', 'ios', 'web'],
    supportedWindows: ['main'],
  },
  // TODO: ELECTRON MIGRATION
  [AppCapability.DesktopMainWindowExclusive]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.SpotlightExclusive]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.MiniChatExclusive]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main'],
  },
  //TODO: ELECTRON MIGRATION
  [AppCapability.BackgroundAuthTokenRefresh]: {
    supportedPlatforms: ['macos', 'android', 'electron'],
    supportedWindows: ['main'],
  },
  [AppCapability.MeetingNotes]: {
    supportedPlatforms: ['macos', 'electron', 'web'],
    supportedWindows: ['main'],
  },
  [AppCapability.RecordMeeting]: {
    supportedPlatforms: ['macos', 'electron'],
    supportedWindows: ['main', 'meeting-status'],
  },
};

/**
 * Checks if a capability is available in the current environment
 * @param capability - The capability to check
 * @returns boolean indicating if the capability is available
 */
export const isCapable = (capability: AppCapability): boolean => {
  const currentPlatform = getCurrentPlatform();
  const systemPlatform = inElectron() ? window.systemInfo?.platform : null;
  const currentWindowLabel = inBrowser()
    ? 'main'
    : (window.systemInfo?.currentWindow?.label ?? 'main');
  const currentWindow =
    Object.values(WindowLabels).find((label) => label === currentWindowLabel) ??
    'main';

  const { supportedPlatforms, supportedWindows, disabled } =
    capabilityConstraintsMap[capability];

  if (disabled) return false;

  if (
    capability === AppCapability.StartupAtLogin &&
    inElectron() &&
    systemPlatform !== 'darwin' &&
    systemPlatform !== 'win32'
  ) {
    return false;
  }

  if (
    (capability === AppCapability.MeetingNotes ||
      capability === AppCapability.RecordMeeting) &&
    !isMeetingNotesSupported()
  )
    return false;

  if (!supportedPlatforms.includes(currentPlatform)) {
    // console.warn(
    //   `Capability ${capability} is not available on platform ${currentPlatform}`
    // );
    return false;
  }

  if (!supportedWindows.includes(currentWindow)) {
    // console.warn(
    //   `Capability ${capability} is not available in window ${currentWindowLabel}`
    // );
    return false;
  }

  return true;
};

export const inDesktop = () => isCapable(AppCapability.DesktopExclusive);
export const isWindows = () =>
  inElectron() && window.systemInfo?.platform === 'win32';
export const isMac = () =>
  inElectron() && window.systemInfo?.platform === 'darwin';
export const isDesktopMainWindow = () =>
  isCapable(AppCapability.DesktopMainWindowExclusive);

/** Returns true if the macOS version is at least `major.minor` */
export function isMacOSAtLeast(major: number, minor: number): boolean {
  if (!inElectron()) return false;
  const v = window.systemInfo?.systemVersion;
  if (!v) return false;
  return isVersionAtLeast(v, major, minor);
}

const isMeetingNotesSupported = () =>
  !isMac() ||
  isMacOSAtLeast(
    MEETING_NOTES_MIN_MACOS_VERSION.major,
    MEETING_NOTES_MIN_MACOS_VERSION.minor
  );

export const getCurrentPlatform = (): SupportedPlatform => {
  if (inElectron()) return 'electron';
  if (inBrowser()) return 'web';
  return 'web';
};

export const getBrowser = async (): Promise<string> => {
  const ua = window.navigator.userAgent;

  if (window.navigator.brave && (await window.navigator.brave.isBrave()))
    return 'Brave';
  if (ua.includes('Edg/')) return 'Microsoft Edge';
  if (ua.includes('Vivaldi')) return 'Vivaldi';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Firefox/')) return 'Mozilla Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Chrome/')) {
    const isArc = getComputedStyle(document.documentElement)
      .getPropertyValue('--arc-palette-title')
      .trim();
    return isArc ? 'Arc Browser' : 'Google Chrome';
  }

  return 'Unknown Browser';
};
